import { Col, Row, DatePicker } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import {
  GetInvoiceCajaExcel,
  GetMatrizAtenciones,
} from "../../../Service/Service.matrizAtenciones";
import DataTable from "../../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../../Components/UI/Chip";
import BotonesExportar from "../../../Components/UI/BotonesExportar";
import { useToast } from "../../../Components/UI/Aviso";
import { usePageSubtitulo } from "../../../context/PageHeaderContext";
import "../../../styles/Resultados.css";
const { RangePicker } = DatePicker;

/* ============================================================================
   SISTEMA-DE-DISENO · Matriz de Atenciones, migrada al kit.

   La consulta, sus filtros y el endpoint del Excel son EXACTAMENTE los de
   antes. Lo que cambia es la lectura: eran 14 columnas con 2000px de ancho
   mínimo -- se leía en scroll horizontal, buscando el dato -- y ahora son 7,
   agrupando los campos que se leen juntos. Ningún dato se pierde.

   El reporte cruza la factura contra la prestación clínica; lo que de verdad
   importa de cada fila es si ese cruce ENCONTRÓ la prestación, así que
   "NO ENCONTRADO" deja de ser un aviso amarillo genérico y pasa a marcarse
   como lo que es: un dato incompleto.
   ========================================================================== */

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const fecha = (v) => (v ? format(new Date(v), "dd/MM/yyyy", { locale: es }) : "");

function MatrizAtenciones() {
  const [filters, setFilters] = useState();
  const [loading, setLoading] = useState(false);
  const [cajaList, setCajaList] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [descargando, setDescargando] = useState(false);
  const { mostrar } = useToast();

  // === 🧩 Definición de columnas ===
  const columns = [
    {
      // El documento y su fecha son el mismo dato para quien busca una factura.
      key: "documento",
      titulo: "Factura",
      ancho: 170,
      render: (r) => <CeldaDoble principal={r.num_documento} secundario={fecha(r.fecha_emision)} monoPrincipal />,
    },
    {
      // Identidad del paciente: nombre y cédula, más la aseguradora que
      // responde por esa atención.
      key: "paciente",
      titulo: "Paciente",
      ancho: 220,
      render: (r) => (
        <CeldaDoble
          principal={r.paciente ? tituloDeNombre(r.paciente) : "Sin determinar"}
          secundario={[r.cedula, r.seguro].filter(Boolean).join(" · ")}
        />
      ),
    },
    {
      // La especialidad es del médico: van juntas.
      key: "doctor",
      titulo: "Médico",
      ancho: 200,
      render: (r) => <CeldaDoble principal={r.doctor || "Sin determinar"} secundario={r.especialidad} />,
    },
    { key: "vendedor", titulo: "Vendedor", ancho: 170, render: (r) => <Celda valor={r.vendedor || "Sin determinar"} /> },
    {
      // Examen con su tipo y categoría, que lo clasifican.
      key: "examen",
      titulo: "Examen",
      ancho: 230,
      render: (r) => (
        <CeldaDoble principal={r.examen_factura} secundario={[r.tipo_examen, r.categoria].filter(Boolean).join(" · ")} />
      ),
    },
    {
      // Los dos valores existen para COMPARARSE: el de la factura manda y el
      // de la prestación va debajo, que es como se lee un cruce.
      key: "valores",
      titulo: "Valor",
      ancho: 150,
      alinear: "derecha",
      render: (r) => (
        <CeldaDoble
          principal={money(r.valor_factura)}
          secundario={r.valor_pcp ? `PCP ${money(r.valor_pcp)}` : "PCP —"}
          monoPrincipal
        />
      ),
    },
    {
      key: "estado_coincidencia",
      titulo: "Coincidencia",
      ancho: 140,
      render: (r) =>
        r.estado_coincidencia === "COINCIDE" ? (
          <ChipEstado tono="hecho">Coincide</ChipEstado>
        ) : (
          // No es un estado del proceso, es un dato que falta: se marca como tal.
          <ChipCategoria incompleto>{r.estado_coincidencia || "Sin determinar"}</ChipCategoria>
        ),
    },
  ];

  // === 🧩 Cargar datos ===
  const fetchInitialData = useCallback(
    async (page = 1, limit = 20, currentFilters) => {
      setLoading(true);
      const result = await GetMatrizAtenciones(page, limit, currentFilters);

      if (result?.success) {
        // Una factura aporta TANTAS filas como detalles tenga, así que
        // `idfactura` no identifica la fila -- se usaba como `key` y React
        // recibía claves repetidas. La clave se compone aquí, en la vista;
        // la consulta no se toca.
        const data = (result.data || []).map((item, i) => ({
          ...item,
          _fila: `${item.idfactura}-${i}`,
        }));
        setCajaList(data);
        setError(null);
        setTotal(result?.pagination?.total || 0);
        setCurrentPage(result?.pagination?.page || 1);
        setPageSize(result?.pagination?.limit || 20);
      } else {
        // Antes la tabla se vaciaba en silencio: un rango inválido y un rango
        // sin atenciones se veían igual.
        setCajaList([]);
        setTotal(0);
        setError(result?.message || "No se pudo obtener la matriz de atenciones.");
      }
      setLoading(false);
    },
    []
  );

  // === 🧩 useEffect — solo se dispara cuando cambian filtros ===
  useEffect(() => {
    fetchInitialData(1, pageSize, filters);
  }, [filters]); // 👈 SOLO filters

  // === 🧩 Filtro por fechas ===
  const handleDateChange = (dates) => {
    setCurrentPage(1);
    if (!dates) {
      setFilters(undefined);
      return;
    }
    const filtrosFinales = {
      rangoFechas: dates,
      fecha_inicial: dates?.[0]?.format("YYYY-MM-DD"),
      fecha_final: dates?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    };
    setFilters(filtrosFinales);
  };

  // === 🧩 Exportar Excel ===
  const handleExcel = async () => {
    setDescargando(true);
    try {
      const response = await GetInvoiceCajaExcel(filters);
      if (response.status === 200) {
        const link = document.createElement("a");
        const blob = response.data;
        const objectURL = URL.createObjectURL(blob);
        link.href = objectURL;
        link.download = `REPORTE_MATRIZ_ATENCIONES.xlsx`;
        link.click();
        URL.revokeObjectURL(objectURL);
      } else {
        mostrar({ tipo: "error", texto: response?.message || "No se pudo generar el Excel." });
      }
    } catch (error) {
      // Antes esto terminaba en la consola: el usuario pulsaba y no pasaba nada.
      mostrar({ tipo: "error", texto: error?.message || "No se pudo generar el Excel." });
    } finally {
      setDescargando(false);
    }
  };

  usePageSubtitulo(loading ? "" : `${total} factura${total === 1 ? "" : "s"}`);

  // === 🧩 Render principal ===
  return (
    <div className="page-content matriz-atenciones-page">
      <div className="container-fluid">
        <Row className="d-flex align-items-center justify-content-between mb-3">
          <Col>
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item">Reportes</li>
              <li className="breadcrumb-item active">Matriz de Atenciones</li>
            </ol>
          </Col>

          <Col className="text-end d-flex justify-content-end align-items-center gap-2 flex-wrap">
            <RangePicker
              value={filters?.rangoFechas}
              onChange={handleDateChange}
              getPopupContainer={() => document.body}
            />
            {/* El archivo lo arma el backend, donde `reportes.export` es una
                barrera real. El rango sigue siendo obligatorio, como antes:
                el botón se apaga, pero ahora dice por qué. */}
            <BotonesExportar
              permiso="reportes.export"
              titulo="Matriz de Atenciones"
              idPrefijo="matriz"
              formatos={["excel"]}
              bloqueo={filters?.rangoFechas ? null : "Elige primero un rango de fechas"}
              cargando={descargando}
              onExcel={handleExcel}
            />
          </Col>
        </Row>

        <DataTable
          columnas={columns}
          filas={cajaList}
          filaKey="_fila"
          cargando={loading}
          error={error}
          onReintentar={() => fetchInitialData(currentPage, pageSize, filters)}
          etiquetaTabla="Matriz de atenciones"
          hayFiltrosAplicados={Boolean(filters?.rangoFechas)}
          onLimpiarFiltros={() => handleDateChange(null)}
          textoVacioSinFiltros="No hay atenciones facturadas todavía."
          paginacion={{
            page: currentPage,
            pageSize,
            total,
            tamanos: [20, 50, 100],
            onCambio: (p, ps) => {
              setCurrentPage(p);
              setPageSize(ps);
              fetchInitialData(p, ps, filters);
            },
          }}
        />
      </div>
    </div>
  );
}

export default MatrizAtenciones;
